import { OrderProps } from "store/modules/Orders/types.store";
import {
  formatCpf as formatCpfDocument,
  formatTaxDocument,
} from "./taxDocument";

export function formatProductNameAndDescription(
  name?: string,
  description?: string,
): string {
  const n = (name ?? "").trim();
  const d = (description ?? "").trim();

  if (!n && !d) return "";
  if (!n) return d;
  if (!d) return n;

  return `${n} - ${d}`;
}

export function formatPlacedOriginLabel(placedOrigin?: string): string {
  if (placedOrigin === "ConsumerApp") {
    return "VIA APLICATIVO";
  }

  if (
    placedOrigin === "DealerWhatsApp" ||
    placedOrigin === "ConsumerWhatsApp"
  ) {
    return "VIA WHATSAPP";
  }

  if (placedOrigin === "DealerStore" || placedOrigin === "DriverStore") {
    return "VIA PORTARIA";
  }

  return "Não informado";
}

export function normalizeForComparison(text: string): string {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

type AddressInput = {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  apartment?: string | null;
  district?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
};

const isFilledText = (value?: string | null): value is string =>
  typeof value === "string" && value.trim().length > 0;

const firstFilled = (...values: (string | null | undefined)[]): string => {
  const match = values.find(isFilledText);
  return match ? match.trim() : "";
};

export function formatStreetAddress(
  address?: AddressInput | null,
  options: { fallback?: string; complementSeparator?: string } = {},
): string {
  const { fallback = "", complementSeparator = " / " } = options;
  if (!address) return fallback;

  const street = firstFilled(address.street);
  const number = firstFilled(address.number);
  const complement = firstFilled(address.complement, address.apartment);

  const streetAndNumber = [street, number].filter(Boolean).join(", ");
  const result = [streetAndNumber, complement]
    .filter(Boolean)
    .join(complementSeparator);

  return result || fallback;
}

export function formatFullAddress(
  address?: AddressInput | null,
  options: { fallback?: string } = {},
): string {
  const { fallback = "" } = options;
  if (!address) return fallback;

  const head = formatStreetAddress(address);
  const district = firstFilled(address.district, address.neighborhood);
  const city = firstFilled(address.city);
  const state = firstFilled(address.state);

  const cityState = [city, state].filter(Boolean).join(", ");
  const tail = [district, cityState].filter(Boolean).join(" - ");

  return [head, tail].filter(Boolean).join(", ") || fallback;
}

export function formatCurrency(currency: string | number): string {
  return Number(currency)?.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export const NUMBERS_REGEX = /[^0-9]/g;

export const EMAIL_PATTERN_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const CPF_PATTERN_REGEX =
  /^(?!000\.000\.000-00|111\.111\.111-11|222\.222\.222-22|333\.333\.333-33|444\.444\.444-44|555\.555\.555-55|666\.666\.666-66|777\.777\.777-77|888\.888\.888-88|999\.999\.999-99)((\d{3}\.){2}\d{3}-\d{2})$/;

/** @deprecated Use taxDocument.formatTaxDocument */
export function cpfOrCnpjFormat(cpfOrCnpj: string | number): string {
  return formatTaxDocument(cpfOrCnpj);
}

export function formatPhoneByText(phone: string) {
  phone = phone.replace(/\D/g, "");
  phone = phone.replace(/^(\d{2})(\d)/g, "($1) $2");
  phone = phone.replace(/(\d)(\d{4})$/, "$1-$2");
  return phone;
}

export function cellPhoneBr(cell: string): string {
  let v = cell.replace(/\D/g, "");

  v = v.replace(/^(\d{2})(\d)/g, "($1) $2");
  v = v.replace(/(\d)(\d{4})$/, "$1-$2");
  return v;
}

export const PHONE_REGEX = /^(\d{2})(\d+)(\d{4})$/;

export const removeCaracterPhone = (phone: string) =>
  phone ? phone.replace(/[^0-9]+/g, "") : "";

/** @deprecated Use taxDocument.formatCpf */
export function maskCpf(cpf: string | undefined) {
  return formatCpfDocument(cpf);
}

export const copyToClipboard = (value: string) => {
  if (navigator.clipboard !== undefined) {
    navigator.clipboard.writeText(value);
    return true;
  }

  return false;
};

export const totalPrices = (data: OrderProps) => {
  let sum = 0;
  if (data) {
    for (let i = 0; i < data.items.length; i += 1) {
      sum += (data.items[i]?.unitPrice || 0) * (data?.items[i]?.quantity || 0);
    }
  }
  return sum;
};

export const voucherFunction = (data: OrderProps) => {
  if (data?.voucher?.campaign) {
    return data?.voucher?.campaign;
  }
  if (data?.voucher?.code) {
    return data?.voucher?.code;
  }
  return "";
};

export const copyDivToClipboard = (id: string) => {
  const range = document.createRange();
  const element = document?.getElementById(id);

  if (element) {
    range.selectNode(element as any);
    window?.getSelection()?.removeAllRanges();
    window?.getSelection()?.addRange(range);
    document.execCommand("copy");
    window?.getSelection()?.removeAllRanges();
  }
};
